import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

/** Paper Preview Card thumb row: 5 placeholder tiles at a ~60×66 ratio. */
const PREVIEW_STRIP_KEYS = ['t0', 't1', 't2', 't3', 't4'] as const;

export type DesignPreviewThumbnailRadius = 'standard' | 'subtle' | 'square';

/** Thumbnail tile radius per corner-rounding choice; `standard` keeps the original `rounded-10`. */
const THUMBNAIL_RADIUS_CLASS: Record<DesignPreviewThumbnailRadius, string> = {
  standard: 'rounded-10',
  subtle: 'rounded-4',
  square: 'rounded-none',
};

export type DesignPreviewThumbnailStripProps = {
  /** Border-radius of the placeholder thumbnail tiles. */
  thumbnailRadius?: DesignPreviewThumbnailRadius;
  /**
   * When true, the strip and its tiles flex to fill the leftover height of a fixed-height card
   * (collection 100px mode). When false (the default), tiles keep their original aspect ratio.
   */
  fill?: boolean;
  /** Dark surface scheme for the tiles; defaults to the original light scheme. */
  isDark?: boolean;
};

/** The row of placeholder thumbnail tiles beneath the title row. */
export const DesignPreviewThumbnailStrip = memo(
  ({ thumbnailRadius = 'standard', fill = false, isDark = false }: DesignPreviewThumbnailStripProps) => (
    <div className={cn('mt-1.5 flex gap-1', fill ? 'min-h-0 flex-1' : 'shrink-0')}>
      {PREVIEW_STRIP_KEYS.map((key) => (
        <div
          key={key}
          className={cn(
            'min-w-0 flex-1',
            // Filled tiles fill the leftover column height; default keeps the original aspect ratio.
            fill ? 'h-full' : 'aspect-[60/66]',
            THUMBNAIL_RADIUS_CLASS[thumbnailRadius],
            isDark ? 'ads:bg-ads-gray-10 bg-gray-10' : 'ads:bg-ads-gray-4 bg-gray-4',
          )}
        />
      ))}
    </div>
  ),
);

DesignPreviewThumbnailStrip.displayName = 'DesignPreviewThumbnailStrip';
